import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
    CoordinateReferenceSystem,
    CoordinateReferenceSystemUpdated,
    CoordinateReferenceSystemOnServerCreated,
    selectCoordinateReferenceSystemsActionLoading,
    selectCoordinateReferenceSystemById,
    selectLastCreatedCoordinateReferenceSystemId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-coordinate-reference-system-edit',
    templateUrl: './coordinate-reference-system-edit.component.html',
    styleUrls: ['./coordinate-reference-system-edit.component.scss'],
})


export class CoordinateReferenceSystemEditComponent implements OnInit, OnDestroy {

    model: CoordinateReferenceSystem;
    oldModel: CoordinateReferenceSystem;

    coordinateReferenceSystemForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    readonly = true

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param layoutUtilsService: LayoutUtilsService
     * @param store: Store<AppState>
     * @param layoutConfigService: LayoutConfigService
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectCoordinateReferenceSystemsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectCoordinateReferenceSystemById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initCoordinateReferenceSystem();
                    }
                });
            } else {
                this.model = new CoordinateReferenceSystem();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initCoordinateReferenceSystem();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Approaches To Values', page: `../default/admin-management/linked-tables/coordinate-reference-systems`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/coordinate-reference-systems/edit/${id}`
                    : `../default/admin-management/linked-tables/coordinate-reference-systems/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initCoordinateReferenceSystem() {
        this.createForm();
    }


    createForm() {
        this.coordinateReferenceSystemForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('COORDINATE_REFERENCE_SYSTEM.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('COORDINATE_REFERENCE_SYSTEM.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.coordinateReferenceSystemForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.coordinateReferenceSystemForm.markAsPristine();
        this.coordinateReferenceSystemForm.markAsUntouched();
        this.coordinateReferenceSystemForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.coordinateReferenceSystemForm.controls;
        /** check form */
        if (this.coordinateReferenceSystemForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateCoordinateReferenceSystem(this.model);
        } else {
            this.createCoordinateReferenceSystem(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: CoordinateReferenceSystemModel
     */
    updateCoordinateReferenceSystem(_model: CoordinateReferenceSystem) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateCoordinateReferenceSystem: Update<CoordinateReferenceSystem> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new CoordinateReferenceSystemUpdated({
            partialCoordinateReferenceSystem: updateCoordinateReferenceSystem,
            coordinateReferenceSystem: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: CoordinateReferenceSystemModel
     */
    createCoordinateReferenceSystem(_model: CoordinateReferenceSystem) {
        this.store.dispatch(new CoordinateReferenceSystemOnServerCreated({coordinateReferenceSystem: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedCoordinateReferenceSystemId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model
            && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }



    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
